import type { MintingDapp } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:54002';

/**
 * Normalize a dapp object from the API.
 * Ensures config and tags are proper objects/arrays, not JSON strings.
 */
function normalizeDapp(dapp: any): MintingDapp {
  let config = dapp.config;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch (e) {
      console.warn('Failed to parse dapp config:', e);
      config = { allowedChains: [19, 14], allowedOrigins: [], sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups', requireAuth: true };
    }
  }

  let tags: string[] = [];
  if (Array.isArray(dapp.tags)) {
    tags = dapp.tags;
  } else if (typeof dapp.tags === 'string') {
    try {
      const parsed = JSON.parse(dapp.tags);
      if (Array.isArray(parsed)) tags = parsed;
    } catch (e) {
      console.warn('Failed to parse dapp tags:', e);
    }
  }

  return {
    ...dapp,
    config,
    tags,
  };
}

/**
 * API client for minting dapps
 * Handles all communication with the backend MySQL database
 */
export const mintingDappAPI = {
  /**
   * Fetch all minting dapps
   */
  async getAll(filters?: {
    type?: 'internal' | 'external';
    author?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: MintingDapp[]; pagination?: any }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...filters })
    });

    const result = await response.json();
    if (result.success && Array.isArray(result.data)) {
      result.data = result.data.map(normalizeDapp);
    }
    return result;
  },

  /**
   * Get single dapp by ID
   */
  async getById(id: string): Promise<{ success: boolean; data: MintingDapp }> {
    // Use the `all` endpoint with ID filter for simplicity
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    });
    const result = await response.json();
    if (result.success && Array.isArray(result.data) && result.data.length > 0) {
      return { success: true, data: normalizeDapp(result.data[0]) };
    }
    return { success: false, data: null as any };
  },

  /**
   * Create new dapp (admin only)
   */
  async create(dapp: Partial<MintingDapp>, authToken: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken
      },
      body: JSON.stringify(dapp)
    });
    return response.json();
  },

  /**
   * Update existing dapp (admin only)
   */
  async update(id: string, dapp: Partial<MintingDapp>, authToken: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken
      },
      body: JSON.stringify(dapp)
    });
    return response.json();
  },

  /**
   * Delete dapp (admin only)
   */
  async delete(id: string, authToken: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': authToken
      }
    });
    return response.json();
  },

  /**
   * Bulk import dapps from JSON (admin only)
   */
  async import(dapps: MintingDapp[], authToken: string): Promise<{
    success: boolean;
    message: string;
    imported: number;
    skipped: number;
    errors?: string[];
  }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken
      },
      body: JSON.stringify({ dapps })
    });
    return response.json();
  },

  /**
   * Export all dapps as JSON (admin only)
   */
  async export(authToken: string): Promise<{ success: boolean; data: MintingDapp[] }> {
    const response = await fetch(`${API_BASE_URL}/api/v1/minting-dapps/export`, {
      method: 'POST',
      headers: {
        'Authorization': authToken
      }
    });
    const result = await response.json();
    if (result.success && Array.isArray(result.data)) {
      result.data = result.data.map(normalizeDapp);
    }
    return result;
  }
};
